import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronsLeft, ChevronsRight, Languages } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuthStore } from '@/stores/auth';
import { filterNavGroups, navGroups } from './nav-config';

export interface SidebarProps {
 /** Controls whether the sidebar is collapsed to icon-only width. */
 collapsed?: boolean;
 /** Toggle handler for the collapse button (desktop only). */
 onToggleCollapse?: () => void;
 /** When provided, the sidebar renders without the desktop collapse button —
 * used inside the mobile drawer where each item should close the menu. */
 onNavigate?: () => void;
 className?: string;
}

/**
 * Primary side navigation. Lists nav groups with role-aware filtering and
 * highlights the active route. Stays accessible via `<nav aria-label>`.
 */
export function Sidebar({
 collapsed = false,
 onToggleCollapse,
 onNavigate,
 className,
}: SidebarProps) {
 const { t } = useTranslation();
 const role = useAuthStore((s) => s.user?.role);
 const groups = filterNavGroups(navGroups, role);

 return (
 <aside
 className={cn(
 'flex h-full flex-col border-r border-border bg-card text-card-foreground ',
 'transition-[width] duration-200 ease-out',
 collapsed ? 'w-[72px]' : 'w-64',
 className,
 )}
 aria-label="Primary"
 >
 <div className="flex h-14 items-center gap-2 border-b border-border px-4">
 <Languages className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
 {!collapsed && (
 <div className="flex flex-col leading-tight"><span className="truncate text-sm font-semibold">Thanh Giang Conincon</span><span className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">TransHub</span></div>
 )}
 </div>

 <nav className="flex-1 space-y-4 overflow-y-auto p-2" aria-label="Sidebar">
 {groups.map((group, idx) => (
 <div key={group.labelKey ?? `top-${idx}`} className="space-y-1">
 {group.labelKey && !collapsed && (
 <p className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
 {t(group.labelKey)}
 </p>
 )}
 {group.labelKey && collapsed && idx > 0 && (
 <Separator className="my-2" />
 )}
 <ul className="space-y-1">
 {group.items.map((item) => (
 <li key={item.to}>
 <NavLink
 to={item.to}
 end={item.to === '/'}
 onClick={onNavigate}
 className={({ isActive }) =>
 cn(
 'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
 'hover:bg-accent hover:text-accent-foreground',
 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
 isActive
 ? 'bg-accent text-accent-foreground'
 : 'text-muted-foreground',
 collapsed && 'justify-center px-2',
 )
 }
 title={collapsed ? t(item.labelKey) : undefined}
 >
 <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
 {!collapsed && (
 <span className="truncate">{t(item.labelKey)}</span>
 )}
 </NavLink>
 </li>
 ))}
 </ul>
 </div>
 ))}
 </nav>

 {onToggleCollapse && (
 <div className="border-t border-border p-2">
 <Button
 variant="ghost"
 size="sm"
 className={cn('w-full justify-start gap-2', collapsed && 'justify-center px-0')}
 onClick={onToggleCollapse}
 aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
 >
 {collapsed ? (
 <ChevronsRight className="h-4 w-4" />
 ) : (
 <>
 <ChevronsLeft className="h-4 w-4" />
 <span className="text-xs">Collapse</span>
 </>
 )}
 </Button>
 </div>
 )}
 </aside>
 );
}
